import { REACT_APP_ENVIRONMENT } from '@partsbadger/utils';

/** Lucky Orange site id for portal.parts-badger.com (production). */
export const LUCKY_ORANGE_SITE_ID = 'f5015ec8';

export interface LuckyOrangeVisitorMeta {
    email?: string;
    name?: string;
    contact_id?: string;
    account?: string;
    is_guest?: boolean;
    [key: string]: string | boolean | undefined;
}

type LuckyOrangeGlobal = {
    $internal: {
        ready: (module: string) => Promise<void>;
    };
    visitor: {
        identify: (userId: string, meta?: LuckyOrangeVisitorMeta) => void;
    };
    events: {
        track: (name: string, meta?: Record<string, unknown>) => void;
    };
    sessions?: {
        getSessionId?: () => string | undefined;
    };
};

declare global {
    interface Window {
        LOQ?: Array<[string, (LO: LuckyOrangeGlobal) => void | Promise<void>]>;
    }
}

export function isLuckyOrangeEnabled(): boolean {
    return REACT_APP_ENVIRONMENT === 'production';
}

function queueLoReady(callback: (LO: LuckyOrangeGlobal) => void | Promise<void>): void {
    if (!isLuckyOrangeEnabled()) {
        return;
    }

    window.LOQ = window.LOQ || [];
    window.LOQ.push([
        'ready',
        function (LO) {
            void Promise.resolve(callback(LO)).catch(err => {
                console.error('[LuckyOrange] ready callback failed:', err);
            });
        },
    ]);
}

export function identifyLuckyOrangeVisitor(userId: string, meta?: LuckyOrangeVisitorMeta): void {
    if (!userId || !isLuckyOrangeEnabled()) {
        return;
    }

    queueLoReady(async LO => {
        await LO.$internal.ready('visitor');
        LO.visitor.identify(userId, meta);
    });
}

export function trackLuckyOrangeEvent(name: string, meta?: Record<string, unknown>): void {
    if (!name || !isLuckyOrangeEnabled()) {
        return;
    }

    queueLoReady(async LO => {
        await LO.$internal.ready('events');
        LO.events.track(name, meta);
    });
}

export function trackPortalAgentOpened(meta?: Record<string, unknown>): void {
    trackLuckyOrangeEvent('Portal Agent Opened', meta);
}

export function trackPortalAgentMessageSent(meta?: Record<string, unknown>): void {
    trackLuckyOrangeEvent('Portal Agent Message Sent', meta);
}

/** Returns the active LO session id when the sessions module is available. */
export function getLuckyOrangeSessionId(): Promise<string | undefined> {
    if (!isLuckyOrangeEnabled()) {
        return Promise.resolve(undefined);
    }

    return new Promise(resolve => {
        queueLoReady(async LO => {
            try {
                await LO.$internal.ready('sessions');
                resolve(LO.sessions?.getSessionId?.());
            } catch {
                resolve(undefined);
            }
        });
    });
}
